import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRangePicker, Spinner } from '@crazyegginc/hatch';
import { Link, useLocation, useParams } from 'react-router-dom';

import { DateRange } from '/src/components/date-time/DateRange';
import { DashboardPage } from '/src/components/Page';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { SitePickerBar } from '/src/components/site-picker';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';
import { DashboardPaywall } from '/src/features/_global/paywalls/DashboardPaywall';
import { FEATURES, TIMEZONES } from '/src/features/_global/constants';
import { goalRefListQuery } from '/src/features/goals/queries';
import { usePermissions, useQueryParams, useSite, useTimeZonedFunctions } from '/src/hooks';
import { JSON_DATE_RANGE_ADAPTER } from '/src/utils/date-range-adapters';

import { DEFAULT_LIMIT, DEFAULT_PRESET_DATE, formatInteger, formatPlatform } from '../constants';
import { adPlatformAdAnalyticsQuery, adPlatformAnalyticsQuery } from '../queries';

const DATE_RANGE_PARAM = 'date_range';
const GOAL_PARAM = 'goal';
const AD_PERFORMANCE_MAX_ALLOWABLE_RANGE = {
  unit: 'days',
  value: 365,
  maxRange: 365,
};

export function AdPerformanceAdPage() {
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.AD_PLATFORM_ANALYTICS);
  const { sites, selectedSite, selectSite, loadingSites } = useSite();
  const { adId } = useParams();
  const { get: getParam, set: setParam } = useQueryParams();
  const { getDefaultDateRange, resolveDateRangeParam } = useTimeZonedFunctions(TIMEZONES.UTC);
  const location = useLocation();
  const adIdNumber = Number.parseInt(adId, 10);
  const dateRangeParam = getParam(DATE_RANGE_PARAM);
  const selectedGoalId = parseOptionalInteger(getParam(GOAL_PARAM));
  const [featureStartedAt, setFeatureStartedAt] = useState(undefined);

  useEffect(() => {
    setFeatureStartedAt(undefined);
  }, [selectedSite?.id]);

  const defaultDateRange = useMemo(
    () =>
      getDefaultDateRange({
        featureStartedAt,
      }),
    [featureStartedAt, getDefaultDateRange],
  );

  const resolvedDateRange = useMemo(
    () =>
      resolveDateRangeParam({
        rawValue: dateRangeParam,
        adapter: JSON_DATE_RANGE_ADAPTER,
        defaultDateRange,
        featureStartedAt,
        maxAllowableRange: AD_PERFORMANCE_MAX_ALLOWABLE_RANGE,
      }),
    [dateRangeParam, defaultDateRange, featureStartedAt, resolveDateRangeParam],
  );

  useEffect(() => {
    if (!resolvedDateRange?.serializedValue) {
      return;
    }

    if (!dateRangeParam) {
      setParam(DATE_RANGE_PARAM, resolvedDateRange.serializedValue);
      return;
    }

    if (resolvedDateRange.shouldRewrite) {
      setParam(DATE_RANGE_PARAM, resolvedDateRange.serializedValue);
    }
  }, [dateRangeParam, resolvedDateRange?.serializedValue, resolvedDateRange?.shouldRewrite, setParam]);

  const dateRangeConfig = resolvedDateRange?.selectedDateRange ?? defaultDateRange;

  const chooseDateRange = useCallback(
    (value) => {
      const serialized = JSON_DATE_RANGE_ADAPTER.serialize(value);

      if (serialized) {
        setParam(DATE_RANGE_PARAM, serialized);
      }
    },
    [setParam],
  );

  const queryDateArgs = useMemo(() => {
    if (!dateRangeConfig) {
      return { presetDate: DEFAULT_PRESET_DATE };
    }

    if (dateRangeConfig.special) {
      return { presetDate: dateRangeConfig.special };
    }

    return {
      startAt: resolvedDateRange?.convertedDateRange?.startDate,
      endAt: resolvedDateRange?.convertedDateRange?.endDate,
    };
  }, [
    dateRangeConfig,
    resolvedDateRange?.convertedDateRange?.endDate,
    resolvedDateRange?.convertedDateRange?.startDate,
  ]);

  const boundaryQuery = useQuery({
    ...adPlatformAnalyticsQuery({
      siteId: selectedSite?.id,
      limit: DEFAULT_LIMIT,
      ...queryDateArgs,
    }),
    enabled: Number.isFinite(selectedSite?.id),
  });

  const nextFeatureStartedAt = useMemo(
    () => getEarliestOldestImportedDataAt(boundaryQuery.data?.adPlatformAnalytics?.connections),
    [boundaryQuery.data?.adPlatformAnalytics?.connections],
  );

  useEffect(() => {
    setFeatureStartedAt((current) => (current === nextFeatureStartedAt ? current : nextFeatureStartedAt));
  }, [nextFeatureStartedAt]);

  const detailQuery = useQuery({
    ...adPlatformAdAnalyticsQuery({
      siteId: selectedSite?.id,
      adId: adIdNumber,
      goalId: selectedGoalId,
      ...queryDateArgs,
    }),
  });

  const goalsQuery = useQuery({
    ...goalRefListQuery({ siteId: selectedSite?.id }),
    enabled: Number.isFinite(selectedSite?.id),
  });

  const loading = loadingSites || (selectedSite && (detailQuery.isLoading || detailQuery.isFetching));
  const detail = detailQuery.data?.adPlatformAdAnalytics;
  const goals = goalsQuery.data?.goalRefList ?? [];
  const backHref = `/ad-performance${location.search || ''}`;

  if (!canViewDashboard) {
    return <DashboardPaywall feature={FEATURES.AD_PLATFORM_ANALYTICS} reason={reason} />;
  }

  return (
    <DashboardPage>
      <SEO title="Ad Performance" />
      <DashHeader title="Ad Performance" />
      <PaymentAlerts />
      <SitePickerBar
        sites={sites}
        selectedSite={selectedSite}
        selectSite={selectSite}
        loading={loadingSites}
        addSiteOption={permissions.can('create', FEATURES.SITES).allowed}
        pageDisplayName="Ad Performance ad detail"
      />
      <div className="flex flex-col gap-6 p-10 !pt-5">
        {loading ? (
          <div className="mt-16 flex w-full items-center justify-center">
            <Spinner />
            <div className="ml-2.5">Loading...</div>
          </div>
        ) : !selectedSite ? null : !Number.isFinite(adIdNumber) ? (
          <StatePanel message="The selected ad could not be loaded." tone="error" />
        ) : detailQuery.error ? (
          <StatePanel message="We couldn't load this ad right now." tone="error" />
        ) : !detail ? (
          <StatePanel message="This ad could not be found for the selected site." tone="error" />
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Link to={backHref} className="text-sm font-medium text-[#00509D] hover:underline">
                  Back to Ad Performance
                </Link>
                <h2 className="mt-3 text-2xl font-semibold text-black-pearl-500">{detail.ad.name}</h2>
                <p className="mt-2 text-sm text-lynch-500">
                  {formatPlatform(detail.ad.platform)}
                  {detail.ad.campaignName ? ` • ${detail.ad.campaignName}` : ''}
                </p>
              </div>
              <div className="text-right text-sm text-lynch-500">
                <div>Ad ID</div>
                <div className="mt-1 font-medium text-black-pearl-500">{detail.ad.platformId || detail.ad.id}</div>
              </div>
            </div>

            <section className="mb-1 flex flex-wrap items-center justify-between gap-4">
              <div className="ml-1 text-lg font-semibold text-black-pearl-500">
                <DateRange dateRange={dateRangeConfig} timeZone={TIMEZONES.UTC} />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <GoalSelect
                  goals={goals}
                  loading={goalsQuery.isLoading || goalsQuery.isFetching}
                  selectedGoalId={selectedGoalId}
                  onChange={(goalId) => setParam(GOAL_PARAM, goalId ? String(goalId) : null)}
                />
                <DateRangePicker
                  lowerBoundary={resolvedDateRange?.pickerProps.lowerBoundary}
                  upperBoundary={resolvedDateRange?.pickerProps.upperBoundary}
                  startDate={resolvedDateRange?.pickerProps.startDate}
                  endDate={resolvedDateRange?.pickerProps.endDate}
                  special={resolvedDateRange?.pickerProps.special}
                  setDateRange={chooseDateRange}
                  setCommonDateRange={chooseDateRange}
                  showCommonRanges={true}
                  size="lg"
                  maxRange={resolvedDateRange?.pickerProps.maxRange}
                  timezone={TIMEZONES.UTC}
                  showTimeZone={true}
                />
              </div>
            </section>

            <section className="grid gap-6 md:grid-cols-3">
              <MetricCard label="Visitors" value={formatInteger(detail.siteMetrics.visitors)} />
              <MetricCard label="Views" value={formatInteger(detail.siteMetrics.views)} />
              <MetricCard
                label="CE Conversions"
                value={formatInteger(detail.siteMetrics.ceConversions)}
                caption={
                  selectedGoalId
                    ? 'Attributed using the selected goal and last-touch pageview matching.'
                    : 'Select a goal to load conversion attribution for this ad.'
                }
              />
            </section>

            {detail.siteMetrics.visitors === 0 &&
            detail.siteMetrics.views === 0 &&
            detail.siteMetrics.ceConversions === 0 ? (
              <StatePanel message="No tracked ad page views matched this ad for the selected range." />
            ) : null}
          </>
        )}
      </div>
    </DashboardPage>
  );
}

function GoalSelect({ goals, loading, selectedGoalId, onChange }) {
  return (
    <label className="flex items-center gap-2 rounded-xl border border-mystic-500 bg-white px-3 py-2 text-sm shadow-sm">
      <span className="font-medium text-black-pearl-500">Goal</span>
      <select
        className="min-w-[180px] bg-transparent text-black-pearl-500 outline-none"
        disabled={loading}
        value={selectedGoalId ?? ''}
        onChange={(event) => onChange(parseOptionalInteger(event.target.value))}
      >
        <option value="">Select goal</option>
        {goals.map((goal) => (
          <option key={goal.id} value={goal.id}>
            {goal.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function MetricCard({ label, value, caption = null }) {
  return (
    <article className="rounded-2xl border border-mystic-500 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">{label}</p>
      <p className="mt-3 text-3xl font-semibold text-black-pearl-500">{value}</p>
      {caption ? <p className="mt-3 text-xs text-lynch-500">{caption}</p> : null}
    </article>
  );
}

function StatePanel({ message, tone = 'default' }) {
  return (
    <div
      className={`rounded-2xl border bg-white px-5 py-6 text-sm shadow-sm ${
        tone === 'error' ? 'border-red-200 text-red-700' : 'border-mystic-500 text-lynch-500'
      }`}
    >
      {message}
    </div>
  );
}

function getEarliestOldestImportedDataAt(connections = []) {
  const oldestImportedDataAts = connections
    .map((connection) => connection?.oldestImportedDataAt)
    .filter(Number.isFinite);

  if (oldestImportedDataAts.length === 0) {
    return undefined;
  }

  return Math.min(...oldestImportedDataAts);
}

function parseOptionalInteger(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}
